import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui';
import { adminStatsService, PlatformOverviewStats } from '@/services/adminStats.service';

interface StatItemProps {
  label: string;
  value: string | number;
  subValue?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  link?: string;
  isLoading?: boolean;
}

const colorClasses = {
  primary: {
    bg: 'bg-primary-50',
    iconBg: 'bg-primary-100',
    text: 'text-primary-600',
    border: 'border-primary-200',
  },
  success: {
    bg: 'bg-success-50',
    iconBg: 'bg-success-100',
    text: 'text-success-600',
    border: 'border-success-200',
  },
  warning: {
    bg: 'bg-warning-50',
    iconBg: 'bg-warning-100',
    text: 'text-warning-600',
    border: 'border-warning-200',
  },
  danger: {
    bg: 'bg-danger-50',
    iconBg: 'bg-danger-100',
    text: 'text-danger-600',
    border: 'border-danger-200',
  },
  info: {
    bg: 'bg-blue-50',
    iconBg: 'bg-blue-100',
    text: 'text-blue-600',
    border: 'border-blue-200',
  },
};

const StatItem: React.FC<StatItemProps> = ({
  label,
  value,
  subValue,
  trend,
  icon,
  color,
  link,
  isLoading,
}) => {
  const colors = colorClasses[color];

  const content = (
    <div
      className={`relative p-5 rounded-xl border ${colors.border} ${colors.bg} transition-all duration-200 ${
        link ? 'hover:shadow-md cursor-pointer' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-secondary-500 truncate">{label}</p>
          {isLoading ? (
            <div className="mt-2 h-8 w-24 bg-secondary-200 rounded animate-pulse" />
          ) : (
            <>
              <p className="mt-1 text-2xl font-bold text-secondary-900">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </p>
              {subValue && (
                <p className="mt-0.5 text-xs text-secondary-500">{subValue}</p>
              )}
            </>
          )}
          {trend && !isLoading && (
            <div className="mt-2 flex items-center">
              {trend.isPositive ? (
                <svg
                  className="w-4 h-4 text-success-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 10l7-7m0 0l7 7m-7-7v18"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4 text-danger-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              )}
              <span
                className={`ml-1 text-xs font-medium ${
                  trend.isPositive ? 'text-success-600' : 'text-danger-600'
                }`}
              >
                {trend.isPositive ? '+' : ''}{trend.value}%
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colors.iconBg}`}>
          <div className={colors.text}>{icon}</div>
        </div>
      </div>
      {link && (
        <div className="absolute bottom-2 right-3">
          <svg
            className={`w-4 h-4 ${colors.text}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      )}
    </div>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }

  return content;
};

interface PlatformStatsProps {
  className?: string;
}

export const PlatformStats: React.FC<PlatformStatsProps> = ({ className = '' }) => {
  const [stats, setStats] = useState<PlatformOverviewStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        const data = await adminStatsService.getPlatformOverview();
        setStats(data);
      } catch (err) {
        setError('Failed to load platform statistics');
        console.error('Error fetching platform stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (error) {
    return (
      <Card className={className}>
        <div className="p-6 text-center">
          <svg
            className="mx-auto h-12 w-12 text-danger-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="mt-2 text-sm text-secondary-600">{error}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 ${className}`}>
      <StatItem
        label="Total Tenants"
        value={stats?.totalTenants ?? 0}
        subValue={`${stats?.activeTenants ?? 0} active / ${stats?.inactiveTenants ?? 0} inactive`}
        trend={
          stats?.tenantGrowthPercentage
            ? { value: stats.tenantGrowthPercentage, isPositive: stats.tenantGrowthPercentage > 0 }
            : undefined
        }
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        }
        color="primary"
        link="/tenants"
        isLoading={isLoading}
      />

      <StatItem
        label="Total Users"
        value={stats?.totalUsers ?? 0}
        trend={
          stats?.userGrowthPercentage
            ? { value: stats.userGrowthPercentage, isPositive: stats.userGrowthPercentage > 0 }
            : undefined
        }
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        }
        color="success"
        link="/users"
        isLoading={isLoading}
      />

      <StatItem
        label="Total Revenue"
        value={`$${(stats?.totalRevenue ?? 0).toLocaleString()}`}
        subValue="QLead fees collected"
        trend={
          stats?.revenueGrowthPercentage
            ? { value: stats.revenueGrowthPercentage, isPositive: stats.revenueGrowthPercentage > 0 }
            : undefined
        }
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
        color="info"
        isLoading={isLoading}
      />

      <StatItem
        label="Platform QLeads"
        value={stats?.totalQLeads ?? 0}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            />
          </svg>
        }
        color="warning"
        isLoading={isLoading}
      />

      <StatItem
        label="Active Subscriptions"
        value={stats?.activeSubscriptions ?? 0}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        }
        color="success"
        isLoading={isLoading}
      />
    </div>
  );
};

export default PlatformStats;
